import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import { GET_DNA_LIST, GET_DNA_SAMPLE_DETAILS, UPLOAD_DNA_RESULT, RESEND_DNA_EMAIL } from "../../services/urls";

// Initial state
const initialState = {
  loading: false,
  dnaSubmissions: [],
  sampleDetails: null,
  error: null,
};

// Async thunk to fetch DNA submissions
export const fetchDnaSubmissions = createAsyncThunk(
  "dnaSample/fetchDnaSubmissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_DNA_LIST);
      // Extract data array from the response structure
      const data = response.data?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch DNA submissions"
      );
    }
  }
);

// Async thunk to fetch DNA sample details by sample ID
export const fetchDnaSampleDetails = createAsyncThunk(
  "dnaSample/fetchDnaSampleDetails",
  async (sampleId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_DNA_SAMPLE_DETAILS}/${sampleId}`);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch DNA sample details"
      );
    }
  }
);

// Async thunk to upload DNA result
export const uploadDnaResult = createAsyncThunk(
  "dnaSample/uploadDnaResult",
  async ({ sampleId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${UPLOAD_DNA_RESULT}/${sampleId}/result`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload DNA result"
      );
    }
  }
);

// Async thunk to resend DNA result email
export const resendDnaEmail = createAsyncThunk(
  "dnaSample/resendDnaEmail",
  async (sampleId, { rejectWithValue }) => {
    try {
      const response = await api.post(`${RESEND_DNA_EMAIL}/${sampleId}/resend`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to resend email"
      );
    }
  }
);

const dnaSampleSlice = createSlice({
  name: "dnaSample",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDnaSubmissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDnaSubmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.dnaSubmissions = action.payload; // Store DNA submissions array
      })
      .addCase(fetchDnaSubmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDnaSampleDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDnaSampleDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.sampleDetails = action.payload;
      })
      .addCase(fetchDnaSampleDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadDnaResult.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDnaResult.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific submission in the list if it exists
        const updatedSubmission = action.payload;
        const index = state.dnaSubmissions.findIndex(sub => sub._id === updatedSubmission._id);
        if (index !== -1) {
          state.dnaSubmissions[index] = updatedSubmission;
        }
      })
      .addCase(uploadDnaResult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendDnaEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendDnaEmail.fulfilled, (state, action) => {
        state.loading = false;
        // No need to update state for resend email, just show success notification
      })
      .addCase(resendDnaEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default dnaSampleSlice.reducer;
